import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { getEventTagLabel } from '@/features/events/eventTags';
import {
  formatPosterDayLabel,
  formatPosterTimeLabel,
  formatWebFeedDistanceLabel,
} from '@/features/events/feed/feedFormatters';
import {
  getEventRSVPButtonLabel,
  getResolvedFeedEntryBadgeLabel,
} from '@/features/events/eventMobileHelpers';
import { FlyerImage } from '@/features/events/shared/FlyerImage';
import { getCoverEventPosterImage } from '@/features/events/shared/FocalEventPosterImage';
import {
  EntryTypeCover,
  EntryTypeFree,
  EntryTypeFreeRSVP,
  EntryTypeRSVP,
  EntryTypeTicketed,
  type FeedEvent,
} from '@/lib/api/apiTypes';
import { getEventHref, getVenueHref } from '@/lib/navigation/routes';
import { router } from 'expo-router';
import { BookmarkIcon } from 'lucide-react-native';
import * as React from 'react';
import { Pressable, View } from 'react-native';

export function WebEventCard({
  event,
  fallbackIndex,
  onRsvp,
  onToggleSaved,
}: {
  event: FeedEvent;
  fallbackIndex: number;
  onRsvp: (event: FeedEvent) => void;
  onToggleSaved: (event: FeedEvent) => void;
}) {
  const posterImage = getCoverEventPosterImage(event);
  const [posterFailed, setPosterFailed] = React.useState(false);
  const showPoster = Boolean(posterImage && !posterFailed);
  const fallbackColor = getEventPosterFallbackColor(fallbackIndex);
  const isGoing = event.viewer_rsvp_status === 'going';
  const isSaved = event.is_saved;
  const rsvpLabel = getEventRSVPButtonLabel(event);
  const entryLabel = getResolvedFeedEntryBadgeLabel(event);
  const distanceLabel = formatWebFeedDistanceLabel(event.distance_meters);
  const eventHref = getEventHref(event.id, event.title, { source: 'web_feed' });
  const venueHref = event.venue_id
    ? getVenueHref(event.venue_id, { source: 'web_feed' }, { name: event.venue_name })
    : null;
  const visibleTags = (event as any).tags?.filter((t: string) => t.trim().length > 0) ?? [];
  const metaTimeLabel = (event as any).doors_at
    ? `Doors ${formatPosterTimeLabel((event as any).doors_at)}`
    : formatPosterTimeLabel(event.starts_at);
  const supportingTag = visibleTags.find((tag: string) => {
    if (!entryLabel) {
      return true;
    }
    return getEventTagLabel(tag).toLowerCase() !== entryLabel.toLowerCase();
  });

  React.useEffect(() => {
    setPosterFailed(false);
  }, [posterImage?.public_url]);

  return (
    <Pressable
      className="relative overflow-hidden rounded-[28px] bg-card active:opacity-95"
      onPress={() => router.push(eventHref as any)}
      style={
        {
          aspectRatio: 4 / 5,
          borderWidth: 2.5,
          borderColor: isGoing ? 'rgba(239,78,46,0.5)' : 'transparent',
          boxShadow: isGoing ? '0 0 24px rgba(239,78,46,0.22)' : '0 10px 24px rgba(0,0,0,0.55)',
        } as any
      }>
      {/* Full-bleed poster image */}
      <View className="absolute inset-0" style={{ backgroundColor: fallbackColor }}>
        {showPoster && posterImage ? (
          <FlyerImage
            image={posterImage}
            style={{ height: '100%', width: '100%' }}
            onError={() => setPosterFailed(true)}
          />
        ) : null}
      </View>

      {/* Top gradient */}
      <View
        className="pointer-events-none absolute left-0 right-0 top-0"
        style={
          {
            height: '16%',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.26) 0%, transparent 100%)',
          } as any
        }
      />

      {/* Bottom gradient */}
      <View
        className="pointer-events-none absolute bottom-0 left-0 right-0"
        style={
          {
            height: '47%',
            background:
              'linear-gradient(to bottom, rgba(4,4,8,0) 0%, rgba(4,4,8,0.42) 42%, rgba(4,4,8,0.82) 78%, rgba(4,4,8,0.95) 100%)',
          } as any
        }
      />

      {/* Top bar: date/time pill + save button */}
      <View className="absolute left-0 right-0 top-0 flex-row items-start justify-between px-[18px] pt-4">
        <View
          className="flex-row items-center rounded-full px-3 py-1.5"
          style={{
            backgroundColor: 'rgba(4,4,8,0.48)',
            borderWidth: 1,
            borderColor: 'rgba(255,255,255,0.10)',
          }}>
          <Text className="text-[10px] font-extrabold uppercase leading-[10px] tracking-[0.4px] text-white">
            {formatPosterDayLabel(event.starts_at)}
          </Text>
          <View className="mx-2 h-2.5 w-px" style={{ backgroundColor: 'rgba(255,255,255,0.20)' }} />
          <Text
            className="text-[10px] font-bold uppercase leading-[10px]"
            style={{ color: 'rgba(240,236,228,0.68)' }}>
            {metaTimeLabel}
          </Text>
        </View>

        <Pressable
          onPress={(pressEvent) => {
            pressEvent?.stopPropagation?.();
            onToggleSaved(event);
          }}
          style={{
            width: 30,
            height: 30,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 15,
            backgroundColor: isSaved ? 'rgba(239,78,46,0.20)' : 'rgba(0,0,0,0.44)',
            borderWidth: 1,
            borderColor: isSaved ? 'rgba(239,78,46,0.40)' : 'rgba(255,255,255,0.10)',
          }}>
          <Icon
            as={BookmarkIcon}
            size={13}
            color={isSaved ? 'rgb(239,78,46)' : 'rgba(240,236,228,0.72)'}
            {...({ fill: isSaved ? 'rgb(239,78,46)' : 'transparent' } as any)}
          />
        </Pressable>
      </View>

      {/* Bottom content: title, meta, chips, RSVP */}
      <View
        className="absolute bottom-0 left-0 right-0 flex-row items-end justify-between"
        style={{ gap: 10, paddingHorizontal: 20, paddingBottom: 18 }}>
        {/* Left: title + meta + chips */}
        <View className="min-w-0 flex-1">
          <Text
            className="text-[19px] font-extrabold leading-[22px] tracking-[-0.3px] text-white"
            numberOfLines={2}>
            {event.title}
          </Text>

          <View className="mt-1 flex-row flex-wrap items-center">
            {distanceLabel ? (
              <>
                <Text
                  className="text-[12px] font-semibold"
                  style={{ color: 'rgba(240,236,228,0.58)' }}>
                  {distanceLabel}
                </Text>
                {event.venue_name ? (
                  <View className="mx-1.5 size-[3px] rounded-full bg-white/30" />
                ) : null}
              </>
            ) : null}
            {venueHref ? (
              <Pressable
                onPress={() => router.push(venueHref as any)}
                hitSlop={8}
                className="min-w-0 shrink">
                <Text
                  className="text-[12px] font-semibold"
                  numberOfLines={1}
                  style={{ color: 'rgba(240,236,228,0.58)' }}>
                  {event.venue_name}
                </Text>
              </Pressable>
            ) : event.venue_name ? (
              <Text
                className="min-w-0 shrink text-[12px] font-semibold"
                numberOfLines={1}
                style={{ color: 'rgba(240,236,228,0.58)' }}>
                {event.venue_name}
              </Text>
            ) : null}
          </View>

          {/* Chips row */}
          <View className="mt-2 flex-row flex-wrap gap-1.5 pr-1">
            {entryLabel ? (
              <View className="rounded-full px-2.5 py-1" style={getEntryBadgeStyle(event.entry_type)}>
                <Text
                  className="text-[10px] font-bold"
                  style={{ color: getEntryBadgeTextColor(event.entry_type) }}>
                  {entryLabel}
                </Text>
              </View>
            ) : null}
            {supportingTag ? (
              <View
                className="rounded-full border px-2.5 py-1"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.055)',
                  borderColor: 'rgba(255,255,255,0.085)',
                }}>
                <Text
                  className="text-[10px] font-semibold"
                  style={{ color: 'rgba(240,236,228,0.48)' }}>
                  {getEventTagLabel(supportingTag)}
                </Text>
              </View>
            ) : null}
          </View>
        </View>

        {/* Right: RSVP button */}
        {!(event as any).is_cancelled ? (
          <View className="shrink-0 pb-1">
            <Pressable
              onPress={(pressEvent) => {
                pressEvent?.stopPropagation?.();
                onRsvp(event);
              }}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 92,
                paddingHorizontal: 12,
                paddingVertical: 6,
                borderRadius: 20,
                backgroundColor: isGoing ? 'rgba(34,197,94,0.2)' : 'rgb(239,78,46)',
                borderWidth: 1,
                borderColor: isGoing ? 'rgba(34,197,94,0.4)' : 'transparent',
              }}>
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: '700',
                  color: isGoing ? 'rgb(34,197,94)' : '#0b0b0b',
                }}>
                {rsvpLabel}
              </Text>
            </Pressable>
          </View>
        ) : null}
      </View>
    </Pressable>
  );
}

function getEventPosterFallbackColor(index: number) {
  const colors = ['#18080f', '#100c1e', '#0c1018', '#0c1208'];
  return colors[index % colors.length];
}

function getEntryBadgeStyle(entryType: string | null): {
  backgroundColor: string;
  borderWidth: number;
  borderColor: string;
} {
  if (entryType === EntryTypeFree || entryType === EntryTypeFreeRSVP) {
    return {
      backgroundColor: 'rgba(52,211,153,0.14)',
      borderWidth: 1,
      borderColor: 'rgba(52,211,153,0.3)',
    };
  }
  if (entryType === EntryTypeCover) {
    return {
      backgroundColor: 'rgba(251,146,60,0.14)',
      borderWidth: 1,
      borderColor: 'rgba(251,146,60,0.3)',
    };
  }
  if (entryType === EntryTypeTicketed) {
    return {
      backgroundColor: 'rgba(167,139,250,0.14)',
      borderWidth: 1,
      borderColor: 'rgba(167,139,250,0.3)',
    };
  }
  if (entryType === EntryTypeRSVP) {
    return {
      backgroundColor: 'rgba(56,189,248,0.14)',
      borderWidth: 1,
      borderColor: 'rgba(56,189,248,0.3)',
    };
  }
  return {
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
  };
}

function getEntryBadgeTextColor(entryType: string | null): string {
  if (entryType === EntryTypeFree || entryType === EntryTypeFreeRSVP) return '#34d399';
  if (entryType === EntryTypeCover) return '#fb923c';
  if (entryType === EntryTypeTicketed) return '#a78bfa';
  if (entryType === EntryTypeRSVP) return '#38bdf8';
  return 'rgba(240,236,228,0.52)';
}
